import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import FullScreenLoader from 'components/FullScreenLoader';
import AuthModal from 'components/AuthModal';
import { STATUS } from 'projectConstants';
import { resetDimension } from 'redux/DimensionPageSlice';
import { resetIndicator } from 'redux/IndicatorPageSlice';
import { resetItem } from 'redux/ItemPageSlice';
import { GetUserModels } from 'redux/ModelsSlice';
import { GetInputAdjustments, resetDriverAdjustmentSlider } from 'redux/driverAdjustmentSlice';
import {
  FetchBlockItemDimensions,
  ShowSidePanel,
  blockDetailStatus,
  blockDriversStatus,
  blockMetadataStatus,
  closeInputSection,
  fetchBlock,
  fetchBlockDrivers,
  fetchBlockMetadata,
  fetchModel,
  filterEndDate,
  filterStartDate,
  getAppliedFilters,
  getBlockDetails,
  getBlockDriversData,
  getBlockMetadata,
  getChartFilter,
  getModelDetails,
  getPlanPageViewMode,
  getTableFilter,
  groupByFilterChange,
  handleSidePanel,
  inputsPageShow,
  modelDetailStatus,
  overallGroupByFilter,
  overallIndicatorFilter,
  overallTimeFilter,
  planPageShow,
  reset,
  setDriver,
  setPlanPageViewMode,
  setSidePanelVisible,
  showAuthModel,
  toggleAuthModel,
} from 'redux/PlanPageSlice';
import {
  fetchAllScenarios,
  fetchScenarioStatus,
  getAllScenarios,
  getBaseScenario,
  getCompareScenario,
  resetScenario,
  selectBaseScenario,
  setCompareChartFilter,
  setCompareTableFilter,
  updateModelandBlock,
} from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import store from 'redux/store/store';
import { useBloxBlockPageTitle } from 'hooks/useBloxDocumentTitle';
import { AppDispatch, getClosestToTargetItemCountDimension } from 'utils/GlobalHelpers';

import { refetchPlanPageFromStore } from '../planPageFetchHelpers';
import {
  selectHasCachedCompareData,
  selectHasCachedPlanData,
  selectIsCompareMode,
  selectIsCompareRefetching,
  selectIsPlanRefetching,
} from '../planPageRevampSelectors';
import { PLAN_INPUT_PANEL_WIDTH } from '../planInputPanelTheme';
import {
  PLAN_CHART_SPLIT_VIEW_HEIGHT,
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_MAIN_CONTENT_GAP,
  PLAN_WHATIFS_PANEL_WIDTH,
  neutral,
} from '../planPageRevampUi';

import PlanChartPanel from './PlanChartPanel';
import PlanCompareChartPanel from './PlanCompareChartPanel';
import PlanCompareDataTable from './PlanCompareDataTable';
import PlanPageTitleBar from './PlanPageTitleBar';
import KpiSummaryBar from './KpiSummaryBar';
import PlanToolbar, { PlanViewMode } from './PlanToolbar';
import InputsPanel from './InputsPanel';
import DriverInputPanel from './DriverInputPanel';
import PlanDataTable from './PlanDataTable';
import PlanWhatIfsPanel from './PlanWhatIfsPanel';
import PlanSoftLoadingOverlay from './PlanSoftLoadingOverlay';

/**
 * Chart section — sizing only. The card shell (border/radius/surface) now
 * lives inside `PlanChartPanel`/`PlanCompareChartPanel` themselves (`PlanCard`),
 * so this wrapper just decides how much of the row the chart gets.
 */
interface PlanChartSectionProperties {
  isCompareMode: boolean;
  viewMode: PlanViewMode;
  onToggleAuthModal: () => void;
}

const PlanChartSection = memo(
  ({ isCompareMode, viewMode, onToggleAuthModal }: PlanChartSectionProperties) => {
    const isChartOnly = viewMode === 'chart';
    const isSplitView = viewMode === 'chart-table';

    return (
      <Box
        flex={isChartOnly || isSplitView ? '1 1 0' : '0 0 auto'}
        flexShrink={isChartOnly || isSplitView ? undefined : 0}
        h={isChartOnly || isSplitView ? '100%' : 'auto'}
        minH={isChartOnly || isSplitView ? 0 : undefined}
        minW={0}
        display='flex'
        flexDirection='column'
        overflow={isSplitView ? 'hidden' : 'visible'}
      >
        {isCompareMode ? (
          <PlanCompareChartPanel
            key={viewMode}
            fillHeight={isChartOnly || isSplitView}
            onToggleAuthModal={onToggleAuthModal}
          />
        ) : (
          <PlanChartPanel
            key={viewMode}
            fillHeight={isChartOnly || isSplitView}
            onToggleAuthModal={onToggleAuthModal}
          />
        )}
      </Box>
    );
  },
);

PlanChartSection.displayName = 'PlanChartSection';

interface PlanTableSectionProperties {
  isCompareMode: boolean;
  onToggleAuthModal: () => void;
  pageScrollMode?: boolean;
}

const PlanTableSection = memo(
  ({ isCompareMode, onToggleAuthModal, pageScrollMode = false }: PlanTableSectionProperties) => (
    <Box
      flex={pageScrollMode ? '1 1 auto' : '1 1 0'}
      minH={pageScrollMode ? undefined : 0}
      minW={0}
      overflow={pageScrollMode ? 'visible' : 'hidden'}
      display='flex'
      flexDirection='column'
      h={pageScrollMode ? 'auto' : undefined}
    >
      {isCompareMode ? (
        <PlanCompareDataTable
          onToggleAuthModal={onToggleAuthModal}
          pageScrollMode={pageScrollMode}
        />
      ) : (
        <PlanDataTable onToggleAuthModal={onToggleAuthModal} pageScrollMode={pageScrollMode} />
      )}
    </Box>
  ),
);

PlanTableSection.displayName = 'PlanTableSection';

const PlanPageContent = () => {
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const initializedBlockReference = useRef<string | null>(null);

  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const compareScenario = useSelector(getCompareScenario);
  const metadata = useSelector(getBlockMetadata);
  const blockDetail = useSelector(getBlockDetails);
  const modelDetail = useSelector(getModelDetails);
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const selectedYear = useSelector(overallTimeFilter);
  const appliedFilters = useSelector(getAppliedFilters);
  const planChartFilter = useSelector(getChartFilter);
  const planTableFilter = useSelector(getTableFilter);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const inputAdjustments = useSelector(GetInputAdjustments);
  const startDatePlanPage = useSelector(filterStartDate);
  const endDatePlanPage = useSelector(filterEndDate);
  const modelStatus = useSelector(modelDetailStatus);
  const blockStatus = useSelector(blockDetailStatus);
  const driversStatus = useSelector(blockDriversStatus);
  const metadataStatus = useSelector(blockMetadataStatus);
  const scenarioStatus = useSelector(fetchScenarioStatus);

  const showInputsPanel = useSelector(ShowSidePanel);
  const showInputPage = useSelector(inputsPageShow);
  const showPlanPage = useSelector(planPageShow);
  const viewMode = useSelector(getPlanPageViewMode) as PlanViewMode;
  const themeColors = useSelector(getPlanPageThemeColors);
  const isAuthModalOpen = useSelector(showAuthModel);
  const isCompareMode = useSelector(selectIsCompareMode);
  const isPlanRefetching = useSelector(selectIsPlanRefetching);
  const isCompareRefetching = useSelector(selectIsCompareRefetching);
  const hasCachedPlanData = useSelector(selectHasCachedPlanData);
  const hasCachedCompareData = useSelector(selectHasCachedCompareData);
  const blockDrivers = useSelector(getBlockDriversData);
  const [showWhatIfsPanel, setShowWhatIfsPanel] = useState(false);

  useBloxBlockPageTitle(blockDetail?.name, modelDetail?.name, 'Plan');

  const hasWhatIfsDrivers = useMemo(
    () => (blockDrivers ?? []).some((driver: { type?: string }) => driver.type === 'indicator'),
    [blockDrivers],
  );

  const onToggleAuthModal = useCallback(() => {
    dispatch(toggleAuthModel());
  }, [dispatch]);

  const onToggleInputsPanel = useCallback(() => {
    if (showInputsPanel && showInputPage) {
      dispatch(closeInputSection());
    }
    dispatch(handleSidePanel());
  }, [dispatch, showInputPage, showInputsPanel]);

  const onCollapseInputsPanel = useCallback(() => {
    dispatch(closeInputSection());
    dispatch(setSidePanelVisible(false));
  }, [dispatch]);

  const onViewModeChange = useCallback(
    (mode: PlanViewMode) => {
      dispatch(setPlanPageViewMode(mode));
    },
    [dispatch],
  );

  const onToggleWhatIfsPanel = useCallback(() => {
    setShowWhatIfsPanel((isOpen) => !isOpen);
  }, []);

  const onCloseWhatIfsPanel = useCallback(() => {
    setShowWhatIfsPanel(false);
  }, []);

  const dateFilterSelected = Boolean(startDatePlanPage && endDatePlanPage);
  const dateFilter = `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`;

  const isLoading =
    modelStatus === STATUS.LOADING ||
    blockStatus === STATUS.LOADING ||
    driversStatus === STATUS.LOADING ||
    metadataStatus === STATUS.LOADING ||
    scenarioStatus === STATUS.LOADING;

  const canFetchData =
    Boolean(blockId && baseScenario?.id && metadata && selectedGroupBy !== null && selectedYear) &&
    !isLoading;

  const showsTable = showPlanPage && (viewMode === 'table' || viewMode === 'chart-table');
  const showsChart = showPlanPage && (viewMode === 'chart' || viewMode === 'chart-table');
  const isSplitAnalytics = viewMode === 'chart-table' && showsChart && showsTable;
  const analyticsScrollReference = useRef<HTMLDivElement>(null);
  const [splitViewportHeight, setSplitViewportHeight] = useState(0);

  const splitChartHeight = useMemo(() => {
    if (splitViewportHeight <= 0) {
      return PLAN_CHART_SPLIT_VIEW_HEIGHT;
    }

    return `${Math.round(splitViewportHeight * 0.7)}px`;
  }, [splitViewportHeight]);

  const splitTableMinHeight =
    splitViewportHeight > 0 ? `${Math.round(splitViewportHeight * 0.3)}px` : null;

  useLayoutEffect(() => {
    if (!isSplitAnalytics) {
      setSplitViewportHeight(0);
      return () => {};
    }

    const element = analyticsScrollReference.current;
    if (!element) {
      return () => {};
    }

    const updateViewportHeight = () => {
      setSplitViewportHeight(element.clientHeight);
    };

    updateViewportHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateViewportHeight);
      return () => window.removeEventListener('resize', updateViewportHeight);
    }

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [isSplitAnalytics, showWhatIfsPanel, showInputsPanel, viewMode]);

  const showSoftOverlay = showPlanPage
    ? isCompareMode
      ? isCompareRefetching && (hasCachedCompareData || inputAdjustments !== null)
      : isPlanRefetching && (hasCachedPlanData || inputAdjustments !== null)
    : false;

  useEffect(() => {
    if (!showPlanPage) {
      setShowWhatIfsPanel(false);
    }
  }, [showPlanPage]);

  useEffect(() => {
    dispatch(GetUserModels({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(reset());
    dispatch(resetScenario());
    dispatch(resetDriverAdjustmentSlider());
    dispatch(setDriver(null));
    dispatch(resetItem());
    dispatch(resetDimension());
    dispatch(resetIndicator());
    setShowWhatIfsPanel(false);
    initializedBlockReference.current = null;
  }, [pathname, dispatch]);

  useEffect(() => {
    if (!blockId || initializedBlockReference.current === blockId) return;
    initializedBlockReference.current = blockId;
    dispatch(setSidePanelVisible(false));
    dispatch(setPlanPageViewMode('chart-table'));
  }, [blockId, dispatch]);

  useEffect(() => {
    if (modelId && blockId) {
      dispatch(updateModelandBlock({ modelId, blockId }));
      dispatch(fetchModel(Number(modelId)));
      dispatch(fetchBlock(Number(blockId)));
      dispatch(fetchAllScenarios(Number(modelId)));
    }
    return () => {
      dispatch(reset());
      dispatch(resetScenario());
      dispatch(resetDriverAdjustmentSlider());
      dispatch(setDriver(null));
      dispatch(resetItem());
      dispatch(resetDimension());
      dispatch(resetIndicator());
    };
  }, [modelId, blockId, dispatch]);

  useEffect(() => {
    if (!modelId || scenarios.length === 0) return;

    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[modelId] || {};

    if (modalFilters?.scenario) {
      const scenarioToSelect =
        typeof modalFilters.scenario === 'object' && modalFilters.scenario.id
          ? scenarios.find((scenario: any) => scenario.id === modalFilters.scenario.id)
          : scenarios.find((scenario: any) => scenario.id === modalFilters.scenario);
      if (scenarioToSelect) {
        dispatch(selectBaseScenario(scenarioToSelect));
        return;
      }
    }

    if (!baseScenario) {
      const defaultScenario = scenarios.find((scenario: any) => scenario.is_base) ?? scenarios[0];
      if (defaultScenario) dispatch(selectBaseScenario(defaultScenario));
    }
  }, [scenarios, baseScenario, modelId, dispatch]);

  useEffect(() => {
    if (metadata && blockId && modelId && selectedGroupBy === null) {
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      const blockGroupBy = modalFilters?.[blockId]?.groupBy
        ? metadata.dimensions?.find((dim: any) => dim.id === modalFilters[blockId].groupBy)
        : null;
      const defaultGroupBy = blockGroupBy
        ? blockGroupBy.id
        : metadata.dimensions && metadata.dimensions.length > 0
        ? getClosestToTargetItemCountDimension(metadata.dimensions)
        : null;
      dispatch(groupByFilterChange(defaultGroupBy || 'Total'));
    }
  }, [metadata, selectedGroupBy, blockId, modelId, dispatch]);

  useEffect(() => {
    if (blockId && baseScenario?.id) {
      dispatch(fetchBlockDrivers({ blockId, parameters: { scenario_id: baseScenario.id } }));
      dispatch(
        fetchBlockMetadata({
          blockId: Number(blockId),
          parameters: { scenario_id: baseScenario.id },
        }),
      );
      dispatch(
        FetchBlockItemDimensions({
          blockId,
          parameters: { params: { scenario_id: baseScenario.id } },
        }),
      );
    }
  }, [blockId, baseScenario?.id, dispatch]);

  useEffect(() => {
    if (!compareScenario?.id) return;
    dispatch(setCompareChartFilter(planChartFilter || 'M'));
    dispatch(setCompareTableFilter(planTableFilter || 'M'));
  }, [compareScenario?.id, planChartFilter, planTableFilter, dispatch]);

  const refetchRequest = useCallback(() => {
    if (!canFetchData) return;
    window.setTimeout(() => {
      refetchPlanPageFromStore(dispatch, store.getState, blockId);
    }, 100);
  }, [blockId, canFetchData, dispatch]);

  useEffect(() => {
    if (!canFetchData) return;
    if (!showPlanPage && !isCompareMode) return;
    refetchRequest();
  }, [
    appliedFilters,
    baseScenario?.id,
    blockId,
    compareScenario?.id,
    dateFilter,
    dateFilterSelected,
    indicatorFilter,
    selectedGroupBy,
    selectedYear,
    canFetchData,
    showPlanPage,
    isCompareMode,
    refetchRequest,
  ]);

  useEffect(() => {
    let timer: number | undefined;

    if (inputAdjustments !== null && blockId && baseScenario?.id) {
      timer = window.setTimeout(() => {
        refetchPlanPageFromStore(dispatch, store.getState, blockId);
      }, 100);
    }

    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [baseScenario?.id, blockId, dispatch, inputAdjustments]);

  if (isLoading) return <FullScreenLoader />;

  return (
    // Transparent, not the old flat #F7F7FB: the page's light gradient is
    // painted once by PlanRevamp/index.tsx (the same value TabContent gives the
    // Dashboard/Dimensions/Blocks tabs). Repainting a solid colour here covered
    // it up, which is why this page was the only revamped module still sitting
    // on a grey canvas.
    <Flex direction='column' flex='1' minH={0} bg='transparent'>
      {/* Figma 76685:5207 — one white strip, title row + divider + toolbar
          row. The KPI row is deliberately NOT inside this box: Figma
          (76685:11650) floats it on the page gradient below the header, as
          its own section, not a third row folded into the header card. */}
      <Box
        flexShrink={0}
        bg={neutral.surface}
        borderBottom='1px solid'
        borderColor={neutral.border}
      >
        <PlanPageTitleBar refetchRequest={refetchRequest} />
        <PlanToolbar
          showInputsPanel={showInputsPanel}
          onToggleInputsPanel={onToggleInputsPanel}
          showWhatIfsPanel={showWhatIfsPanel}
          onToggleWhatIfsPanel={onToggleWhatIfsPanel}
          hasWhatIfsDrivers={hasWhatIfsDrivers}
          showAnalytics={showPlanPage}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          refetchRequest={refetchRequest}
        />
      </Box>

      <Flex flex='1' minH={0} minW={0} overflow='hidden' bg='transparent'>
        {/* Always mounted, width-animated rather than mount/unmount — an
            instant conditional render is what made the panel snap open and
            shut with no transition. `overflow='hidden'` clips the panel's
            own fixed width during the collapse animation. */}
        <Box
          w={showInputsPanel ? PLAN_INPUT_PANEL_WIDTH : '0px'}
          minW={showInputsPanel ? PLAN_INPUT_PANEL_WIDTH : '0px'}
          flexShrink={0}
          overflow='hidden'
          transition='width 0.22s ease, min-width 0.22s ease'
        >
          <InputsPanel onCollapse={onCollapseInputsPanel} />
        </Box>
        {showInputPage && (
          <Box
            flex={showPlanPage ? undefined : '1'}
            minW={showPlanPage ? '360px' : 0}
            maxW={showPlanPage ? '640px' : undefined}
            minH={0}
            display='flex'
            flexDirection='column'
            overflow='hidden'
          >
            <DriverInputPanel refetchRequest={refetchRequest} />
          </Box>
        )}

        {showPlanPage && (
          <Box
            flex='1 1 0'
            minW={0}
            minH={0}
            overflow='hidden'
            display='flex'
            gap={PLAN_MAIN_CONTENT_GAP}
          >
            {/* Padding lives on this scrolling box itself, not on a
                non-scrolling wrapper around it — a wrapper's padding sits
                *outside* the scroll container, so the scrollbar renders at
                the wrapper's inner edge, floating in a gap the width of that
                padding instead of hugging the box it actually scrolls. */}
            <Box
              ref={analyticsScrollReference}
              flex='1 1 0'
              minW={0}
              minH={0}
              overflowY='auto'
              overflowX='hidden'
              position='relative'
              paddingRight={
                showWhatIfsPanel && hasWhatIfsDrivers ? 4 : PLAN_HEADER_HORIZONTAL_PADDING
              }
              paddingLeft={showInputsPanel ? 4 : PLAN_HEADER_HORIZONTAL_PADDING}
              pt={3}
              pb='24px'
            >
              <PlanSoftLoadingOverlay isVisible={showSoftOverlay} color={themeColors.primary} />
              {/* A real flex column, not two independent blocks stacked by
                  document flow. `KpiSummaryBar` lives inside this scrollable
                  box now (scrolls away with the rest of the content). This
                  wrapper needs `h='100%'`, not `minH='100%'` — a `minH` gives
                  no *definite* height for the `h='100%'` chart section below
                  to resolve against, so the browser silently falls back to
                  auto-sizing and every downstream `minH` (including
                  `PLAN_CHART_VIEW_HEIGHT`) was never actually applied. With a
                  definite `h`, the content Flex's own `minH` can still push
                  it — and this wrapper — taller than the viewport when it
                  needs to, which is what lets a card grow and scroll into
                  view instead of being squeezed to fit. */}
              <Flex direction='column' h='100%'>
                <Box flexShrink={0}>
                  <KpiSummaryBar />
                </Box>
                {isSplitAnalytics ? (
                  <Flex direction='column' flex='1 1 auto' gap={PLAN_MAIN_CONTENT_GAP}>
                    <Box
                      flex='0 0 auto'
                      h={splitChartHeight}
                      minH={splitChartHeight}
                      display='flex'
                    >
                      <PlanChartSection
                        isCompareMode={isCompareMode}
                        viewMode={viewMode}
                        onToggleAuthModal={onToggleAuthModal}
                      />
                    </Box>
                    <Box
                      flex='0 0 auto'
                      {...(splitTableMinHeight ? { minH: splitTableMinHeight } : {})}
                      display='flex'
                    >
                      <PlanTableSection
                        isCompareMode={isCompareMode}
                        onToggleAuthModal={onToggleAuthModal}
                        pageScrollMode
                      />
                    </Box>
                  </Flex>
                ) : (
                  <Flex
                    direction='column'
                    flex='1 1 auto'
                    minH={
                      showsChart && !showsTable
                        ? `calc(${PLAN_CHART_VIEW_HEIGHT} + 140px)`
                        : undefined
                    }
                  >
                    {showsChart && (
                      <PlanChartSection
                        isCompareMode={isCompareMode}
                        viewMode={viewMode}
                        onToggleAuthModal={onToggleAuthModal}
                      />
                    )}
                    {showsTable && (
                      <PlanTableSection
                        isCompareMode={isCompareMode}
                        onToggleAuthModal={onToggleAuthModal}
                      />
                    )}
                  </Flex>
                )}
              </Flex>
            </Box>
            {hasWhatIfsDrivers && (
              <Box
                w={showWhatIfsPanel ? PLAN_WHATIFS_PANEL_WIDTH : '0px'}
                minW={showWhatIfsPanel ? PLAN_WHATIFS_PANEL_WIDTH : '0px'}
                flexShrink={0}
                overflow='hidden'
                transition='width 0.22s ease, min-width 0.22s ease'
              >
                <PlanWhatIfsPanel
                  onToggleAuthModal={onToggleAuthModal}
                  onClose={onCloseWhatIfsPanel}
                />
              </Box>
            )}
          </Box>
        )}
      </Flex>
      <AuthModal isOpen={isAuthModalOpen} onClose={onToggleAuthModal} />
    </Flex>
  );
};

export default memo(PlanPageContent);
